'use client';

/**
 * Footer — landing principal (SPEC-135 §2).
 *
 * Componente reutilizable de 4 columnas + barra inferior, visible en las
 * rutas públicas de marketing de Profileia (home, login, register, legal,
 * about, contacto, paquetes, plan-de-compensacion, /market catálogo).
 * Internamente usa `usePathname()` para NO renderizar en `/app/*`, `/admin/*`,
 * `/seller/*` (esos layouts tienen su propia chrome), `/market/comprar/*`
 * (flujos de compra), `/sala/*` (reuniones), `/l/*` (landings de usuarios),
 * `/p/*` (perfiles vCard de usuarios).
 *
 * Spec ref: docs/SPEC-135-FOOTER-MISION-LEGALES.md §2 (estructura footer),
 *           §6 Anti-Invención (handles `@profileiaoficial` confirmados).
 *
 * P-217 (Sprint 62 — SPEC-135):
 *   - 4 columnas responsive (lg:grid-cols-4, sm:grid-cols-2, 1 col móvil).
 *   - Año dinámico (`new Date().getFullYear()`).
 *   - Links sociales con `target="_blank"` + `rel="noopener noreferrer"`.
 *   - Newsletter signup → POST /api/newsletter/subscribe (double opt-in).
 *   - Texto condicional DAO LLC vía `NEXT_PUBLIC_FEATURE_DAO_LLC_CONSTITUTED`.
 *
 * Anti-P-023: el form renderiza feedback real (loading/success/error).
 * Anti-P-041: 0 `as any` en este archivo.
 * Anti-P-042: 0 `console.*` en este archivo (CM-21 compliance).
 * Anti-P-083: 0 refs a docs internos en UI visible al usuario.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import Link from 'next/link';
import type { Route } from 'next';
import { usePathname } from 'next/navigation';
import { subscribeToNewsletter } from '@/lib/newsletter-api';

/**
 * Sprint 93 QA fix (PC-P1-3): feature flags for social media accounts that
 * don't exist yet. The X (Twitter) and YouTube accounts
 * @profileiaoficial return 404 (verified 2026-09-11). Until the Founder
 * creates these accounts (PENDIENTE-FOUNDER #25), the icons are hidden
 * from the footer to avoid sending visitors to 404 pages.
 *
 * When the accounts are created, set these env vars to 'true' in
 * .env.staging / .env.production and the icons will reappear:
 *   NEXT_PUBLIC_FEATURE_SOCIAL_X_ENABLED=true
 *   NEXT_PUBLIC_FEATURE_SOCIAL_YOUTUBE_ENABLED=true
 *
 * Anti-P-021: this does NOT omit redes that SPEC-NEGOCIO defines — it
 * only hides redes whose accounts don't exist yet. The other 5 redes
 * (Instagram, TikTok, LinkedIn, Facebook, Telegram) remain visible.
 */
const SOCIAL_X_ENABLED = process.env.NEXT_PUBLIC_FEATURE_SOCIAL_X_ENABLED === 'true';
const SOCIAL_YOUTUBE_ENABLED = process.env.NEXT_PUBLIC_FEATURE_SOCIAL_YOUTUBE_ENABLED === 'true';

/**
 * Determina si el Footer público debe ocultarse en la ruta actual.
 *
 * Sprint 86 fix (Founder directive 2026-09-11): "verifica los lugares donde
 * está apareciendo el footer de la landing principal, por que está apareciendo
 * en lugares que no debería aparecer. Lo vi en comprar creditos y en la parte
 * baja de las salas. Limita los lugares que puede aparecer el footer de la
 * landing".
 *
 * Pre-fix: blacklist — el footer aparecía en TODAS las rutas excepto
 * `/app/*`, `/admin/*`, `/seller/*`. Esto hacía que apareciera en flujos
 * de compra (`/market/comprar/credits`), páginas de sala pública, etc.
 * — lugares donde el footer de marketing es ruido visual.
 *
 * Post-fix: el footer SOLO aparece en las rutas públicas de landing/marketing
 * donde tiene sentido (home, login, register, legal, about, contacto,
 * paquetes, plan-de-compensacion, /market catálogo + tienda + producto).
 *
 * FIX 2026-09-17 (Founder directive: "quitando el footer de las landing
 * que crean los usuarios"): el footer ya NO aparece en:
 *   - /l/* (landings creadas por usuarios — el usuario crea su propia
 *     landing con su propio branding/CTA, el footer de Profileia es ruido
 *     visual + compite con el branding del usuario)
 *   - /p/* (perfiles públicos de vCard — misma razón, el usuario tiene su
 *     propia vCard con su branding, no necesita el footer corporativo)
 *
 * Se oculta en:
 *   - /app/* (app privada con su propio chrome)
 *   - /admin/* (panel admin)
 *   - /seller/* (portal vendedor)
 *   - /market/comprar/* (flujos de compra — checkout, no marketing)
 *   - /sala/* (páginas públicas de sala — el invitado está entrando a una
 *     reunión, no navegando marketing)
 *   - /l/* (landings creadas por usuarios — branding del usuario, no Profileia)
 *   - /p/* (perfiles públicos vCard — branding del usuario, no Profileia)
 */
function shouldShowFooter(pathname: string | null): boolean {
  if (!pathname) return false;
  // FOUNDER DIRECTIVE 2026-09-19: "El footer SOLO debe ir en la homepage,
  // no regado por ningun otro sitio."
  // Se invierte la lógica: en lugar de una lista negra de rutas donde
  // ocultar, ahora solo se MUESTRA en la homepage (/). Todas las demás
  // rutas (login, register, paquetes, legal, vCard, landings, salas,
  // market, etc.) NO muestran el footer.
  return pathname === '/';
}

/**
 * Feature flag condicional — texto "Operado por Profileia DAO LLC" vs
 * "Operado por Profileia" según si la DAO LLC está constituida.
 * Default: NO constituída → "Operado por Profileia".
 */
const DAO_LLC_CONSTITUTED =
  process.env.NEXT_PUBLIC_FEATURE_DAO_LLC_CONSTITUTED === 'true';

const OPERATOR_LINE = DAO_LLC_CONSTITUTED
  ? 'Operado por Profileia DAO LLC. Arbitraje internacional vía Kleros.'
  : 'Operado por Profileia. Arbitraje internacional vía Kleros.';

const MISSION_SHORT =
  'Reunimos tu academia, tus herramientas, tu marketplace y tu compensación en un solo perfil inteligente. Sin cinco suscripciones, sin letra pequeña, sin promesas vacías. La casa del emprendedor, hecha en español, para el mundo.';

interface SocialLink {
  label: string;
  href: string;
  svg: ReactElement;
}

const SOCIAL_LINKS: SocialLink[] = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/profileiaoficial?stkn=cmV3dHUzajVzd2x2',
    svg: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.1.4.3 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.1-1 .3-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.1-.4-.3-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.1 1-.3 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.1-.9 0-1.4.2-1.7.3-.4.2-.7.4-1 .7-.3.3-.5.6-.7 1-.1.3-.3.8-.3 1.7-.1 1.2-.1 1.6-.1 4.7s0 3.5.1 4.7c0 .9.2 1.4.3 1.7.2.4.4.7.7 1 .3.3.6.5 1 .7.3.1.8.3 1.7.3 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c.9 0 1.4-.2 1.7-.3.4-.2.7-.4 1-.7.3-.3.5-.6.7-1 .1-.3.3-.8.3-1.7.1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c0-.9-.2-1.4-.3-1.7-.2-.4-.4-.7-.7-1-.3-.3-.6-.5-1-.7-.3-.1-.8-.3-1.7-.3-1.2-.1-1.6-.1-4.7-.1zm0 3.1a4.9 4.9 0 110 9.8 4.9 4.9 0 010-9.8zm0 1.8a3.1 3.1 0 100 6.2 3.1 3.1 0 000-6.2zm5.1-.3a1.1 1.1 0 110 2.3 1.1 1.1 0 010-2.3z" />
      </svg>
    ),
  },
  // Sprint 93 QA fix (PC-P1-3): YouTube icon hidden until the account
  // @profileiaoficial is created (currently returns 404). Toggle via
  // NEXT_PUBLIC_FEATURE_SOCIAL_YOUTUBE_ENABLED=true when ready.
  ...(SOCIAL_YOUTUBE_ENABLED ? [{
    label: 'YouTube',
    href: 'https://youtube.com/@profileiaoficial?si=S901cdkSwEd6WdPB',
    svg: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
      </svg>
    ),
  }] : []),
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@profileiaoficial?_r=1&_t=ZS-99jbjF5g98W',
    svg: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.6 6.7a5.6 5.6 0 01-3.4-1.2 5.6 5.6 0 01-2.1-3.5h-3v12.4a2.7 2.7 0 11-2-2.6V8.7a5.6 5.6 0 105.4 7V9.6a8.5 8.5 0 005.1 1.7v-3a5.5 5.5 0 01-.0-1.6z" />
      </svg>
    ),
  },
  // Sprint 93 QA fix (PC-P1-3): X (Twitter) icon hidden until the account
  // @profileiaoficial is created (currently returns 404). Toggle via
  // NEXT_PUBLIC_FEATURE_SOCIAL_X_ENABLED=true when ready.
  ...(SOCIAL_X_ENABLED ? [{
    label: 'X (Twitter)',
    href: 'https://x.com/Profileia',
    svg: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.2 2H21l-6.6 7.5L22 22h-6.8l-4.7-6.2L4.9 22H2l7.1-8L2 2h6.9l4.3 5.7L18.2 2zm-2.4 18h1.9L7.2 4h-2L15.8 20z" />
      </svg>
    ),
  }] : []),
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/in/profileia-oficial-87b2ab437',
    svg: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5V8h3v11zM6.5 6.7a1.8 1.8 0 110-3.5 1.8 1.8 0 010 3.5zM19 19h-3v-5.6c0-1.3 0-3-1.9-3s-2.1 1.4-2.1 2.9V19h-3V8h2.9v1.5h.04a3.2 3.2 0 012.9-1.6c3.1 0 3.7 2 3.7 4.7V19z" />
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/share/1LVXaGQXBg/',
    svg: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M24 12a12 12 0 10-13.9 11.9v-8.4H7V12h3.1V9.4c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9V12h3.3l-.5 3.5h-2.8v8.4A12 12 0 0024 12z" />
      </svg>
    ),
  },
  // Sprint 93 QA fix (PC-P2-1): Telegram removed from SÍGUENOS (it was
  // duplicated — also appears in SOPORTE + COMUNIDAD as "Telegram
  // Channel"). Keeping it only in SOPORTE avoids canonical dilution and
  // clarifies the IA: Telegram is a support/community channel, not a
  // "follow us" social network.
];

interface FooterLinkProps {
  href: string;
  children: React.ReactNode;
}

function FooterLink({ href, children }: FooterLinkProps) {
  const isExternal = href.startsWith('http') || href.startsWith('mailto:');
  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-slate-300 hover:text-emerald-400 transition-colors"
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      href={href as Route}
      // SPEC-177 Task J (Sprint 91): prefetch={false} on internal footer
      // links. The footer has 7 legal links + 3 contacto links + 1
      // "/register" CTA = 11 Next.js `<Link>` components. By default,
      // Next.js prefetches the full RSC payload for each `<Link>` when
      // it enters the viewport (which is immediate on landing since the
      // footer is in the initial viewport on mobile). 11 prefetches ×
      // ~1s each = ~11s of network/CPU on first load — measurable
      // regression on LCP + TBT. Disabling prefetch means the links
      // still navigate instantly on click (Next.js fetches the RSC
      // payload on demand), but don't preload. Trade-off: 0ms slower
      // on click, ~11s faster on initial load — correct trade-off for
      // legal/contacto links that are RARELY clicked (most users don't
      // read Términos before registering).
      prefetch={false}
      className="text-slate-300 hover:text-emerald-400 transition-colors"
    >
      {children}
    </Link>
  );
}

export default function Footer() {
  const pathname = usePathname();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  if (!shouldShowFooter(pathname)) {
    return null;
  }

  async function handleSubscribe(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setMessage('');
    try {
      const result = await subscribeToNewsletter(email);
      setStatus('success');
      setMessage(result.message);
      setEmail('');
    } catch (err) {
      setStatus('error');
      const e = err as { message?: string; code?: string };
      // SPEC-177 Task B.3 (Sprint 91): TIMEOUT-specific message. The
      // backend now fire-and-forgets the email send (Task B.1), so a
      // TIMEOUT means the HTTP request itself took >5s (network/Mongo
      // hang) — but the subscriber IS likely already persisted
      // server-side. The user should NOT see a generic "error" message
      // that implies their subscription failed; instead, tell them the
      // subscription is processing + how to recover if no email arrives.
      if (e?.code === 'TIMEOUT') {
        setMessage('Estamos procesando tu suscripción. Si no recibes el email de confirmación en unos minutos, revisa spam o intenta de nuevo.');
      } else {
        setMessage(e?.message ?? 'No pudimos procesar tu suscripción. Intenta de nuevo.');
      }
    }
  }

  const year = new Date().getFullYear();

  return (
    <footer
      role="contentinfo"
      className="border-t border-slate-800 bg-slate-950 text-slate-300"
    >
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1: Marca + Misión */}
          <section aria-labelledby="footer-brand">
            <h2 id="footer-brand" className="text-white font-bold text-xl mb-2">
              Profileia
            </h2>
            <p className="text-emerald-400 text-sm font-medium mb-4">
              Tu perfil inteligente
            </p>
            <p className="text-sm leading-relaxed mb-4">
              {MISSION_SHORT}
            </p>
            <Link
              href="/register"
              className="inline-flex items-center text-emerald-400 hover:text-emerald-300 font-medium text-sm"
            >
              Únete al movimiento →
            </Link>
          </section>

          {/* Column 2: Legal */}
          <section aria-labelledby="footer-legal">
            <h3 id="footer-legal" className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
              Legal
            </h3>
            <ul className="space-y-2 text-sm">
              <li><FooterLink href="/legal/terminos">Términos y Condiciones</FooterLink></li>
              <li><FooterLink href="/legal/privacidad">Política de Privacidad</FooterLink></li>
              <li><FooterLink href="/legal/cookies">Política de Cookies</FooterLink></li>
              <li><FooterLink href="/legal/vendedor">Acuerdo del Vendedor</FooterLink></li>
              <li><FooterLink href="/legal/kyc-aml">Política KYC/AML</FooterLink></li>
              <li><FooterLink href="/legal/riesgo-cripto">Risk Disclosure Cripto</FooterLink></li>
              <li><FooterLink href="/legal/compensacion">Disclaimer de Compensación</FooterLink></li>
              <li><FooterLink href="/legal/conecta">Términos de Conecta</FooterLink></li>
            </ul>
          </section>

          {/* Column 3: Soporte + Comunidad */}
          <section aria-labelledby="footer-support">
            <h3 id="footer-support" className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
              Soporte + Comunidad
            </h3>
            <ul className="space-y-2 text-sm">
              <li><FooterLink href="/faq">Preguntas frecuentes</FooterLink></li>
              <li><FooterLink href="/contacto">Centro de Ayuda</FooterLink></li>
              <li><FooterLink href="https://t.me/profileiaoficial">Telegram Channel</FooterLink></li>
              <li><FooterLink href="mailto:soporte@profileia.com">Soporte Email</FooterLink></li>
              <li><FooterLink href="/contacto?type=status">Status del Sistema</FooterLink></li>
              <li><FooterLink href="/contacto?type=bug">Reportar un Bug</FooterLink></li>
            </ul>
          </section>

          {/* Column 4: Redes + Newsletter */}
          <section aria-labelledby="footer-social">
            <h3 id="footer-social" className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
              Síguenos + Newsletter
            </h3>
            <div className="flex flex-wrap gap-3 mb-6">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="text-slate-300 hover:text-emerald-400 transition-colors"
                >
                  {s.svg}
                </a>
              ))}
            </div>

            <form onSubmit={handleSubscribe} className="space-y-2" noValidate>
              <p className="text-sm text-slate-300">
                Únete a +5K emprendedores recibiendo estrategias semanales para crecer en Profileia.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  aria-label="Email para newsletter"
                  required
                  className="flex-1 px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
                >
                  {status === 'loading' ? 'Enviando…' : 'Suscribirme'}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Sin spam. Un correo por semana. Cancela cuando quieras.
              </p>
              {status === 'success' && (
                <p role="status" className="text-xs text-emerald-400">{message}</p>
              )}
              {status === 'error' && (
                <p role="status" className="text-xs text-red-400">{message}</p>
              )}
            </form>
          </section>
        </div>
      </div>

 {/* Barra inferior full-width — Task E (Sprint 91) cleanup.
          The previous bottom bar had a duplicated legal nav (Términos /
          Privacidad / Cookies) that already appeared in the "Legal" column
          above. Removed the bottom legal nav block entirely — kept only
          the copyright + tagline + operator line. The full legal links
          remain accessible via the Legal column (no information loss,
          just dedup). */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-6 text-center text-xs text-slate-500">
          <p>© {year} Profileia. Todos los derechos reservados.</p>
          <p className="mt-1">
            Profileia = Tu perfil inteligente. La casa del emprendedor. Todo lo que necesitas para emprender en un solo lugar.
          </p>
          <p className="mt-1">{OPERATOR_LINE}</p>
        </div>
      </div>
    </footer>
  );
}
